import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import store from '@/store';
import {ProfileContestantModel, ProfileModel, UserModel, UserProfileModel} from '@/models/user.model';
import {UserApi} from '@/api/user.api';
import {TablePlatformSettingsModel} from '@/models/platform-settings.model';
import {SkillModel} from '@/models/skill.model';
import {SkillApi} from '@/api/skill.api';
import {PlatformSettingsApi} from '@/api/platform-settings.api';
import {take} from 'rxjs/operators';

export const featureKey = 'user';

// @ts-ignore
if (store.state.user) {
  store.unregisterModule(featureKey)
}

@Module({
  store,
  dynamic: true,
  name: featureKey,
  namespaced: true,
  stateFactory: true
})
export class UserModule extends VuexModule {
  private _user: UserModel | null = null;
  private _userHasProfile: boolean = false;
  private _userHasContestant: boolean = false;
  private _userHasPlatformSettings: boolean = false;
  private _user_id: number | null = null;
  private _user_profile_id: number | null = null;
  private _contestant_id: number | null = null;
  private _platformSettings?: TablePlatformSettingsModel;

  /**
   * Getters
   */
  get hasProfile() {
    return this._userHasContestant;
  }

  get hasPlatformSettings() {
    return this._userHasPlatformSettings;
  }

  get user_id(): number | null {
    return this._user_id;
  }

  get user_profile_id(): number | null {
    return this._user_profile_id;
  }

  get contestant_id(): number | null {
    return this._contestant_id;
  }

  get user(): UserModel | null {
    return this._user;
  }

  get canAcceptChallenges(): boolean {
    return this._userHasContestant;
  }

  get profile(): UserProfileModel | null {
    return (this.user && this.user.user_profile) || null;
  }

  get platformSettings(): TablePlatformSettingsModel | null {
    return (this._user && this._user.platform_setting) || null;
  }

  get skills(): SkillModel[] | null {
    return this._user && this._user.contestant && (
      this._user.contestant.skills
    ) || null;
  }

  get contestant(): ProfileContestantModel | null {
    return (this.user && this.user.contestant) || null;
  }

  get timezone(): string {
    return this._user?.platform_setting?.timezone || 'UTC'
  }

  /**
   * Mutations
   */

  @Mutation
  setUser(user: UserModel) {
    if (!user) {
      // User is not sign-in
      this._user = null;
      this._userHasContestant = false;
      this._userHasPlatformSettings = false;
      this._userHasProfile = false;
      return;
    }

    this._user = user;

    this._user.contestant = user.contestant || new ProfileContestantModel();
    this._userHasContestant = !!user.user_profile && !!user.contestant?.id;

    this._user.user_profile = user.user_profile || new UserProfileModel();
    this._userHasProfile = !!user.user_profile;

    this._user.platform_setting = user.platform_setting || new TablePlatformSettingsModel();
  }

  @Mutation
  userIdLoaded(data: UserModel): void {
    if (data.id) {
      this._user_id = data.id;
      this.context.dispatch('requestUserProfile')
    }
  }

  @Mutation
  profileIdsLoaded(data: { user_profile_id: number, contestant_id: number }): void {
    this._contestant_id = data.contestant_id;
    this._user_profile_id = data.user_profile_id;
  }

  @Action({commit: 'userIdLoaded', rawError: true})
  async registerUser(user: UserModel) {
    return await (new UserApi())
      .createUser(user)
      .pipe(take(1))
      .toPromise()
  }

  @Mutation
  platformSettingsLoaded(settings: TablePlatformSettingsModel) {
    this._platformSettings = settings;
  }

  /**
   * Actions
   */

  @Action({commit: 'setUser', rawError: true})
  async setProfile(data: ProfileModel): Promise<UserProfileModel> {
    if (data.id) {
      return await (new UserApi())
        .updateProfile(data)
        .pipe(take(1))
        .toPromise();
    }

    return await (new UserApi())
      .createProfile(data)
      .pipe(take(1))
      .toPromise();
  }

  @Action({commit: 'profileIdsLoaded', rawError: true})
  async registerProfileContestant(data: {
    user_id: number,
    profile: ProfileContestantModel,
  })
    : Promise<{
    user_profile_id: number,
    contestant_id: number,
  }> {
    return await (new UserApi())
      .registerProfile(data.user_id, data.profile)
      .pipe(take(1))
      .toPromise()
  }

  @Action({commit: 'platformSettings', rawError: true})
  async registerPlatformSettings(data: {
    user_id: number,
    settings: TablePlatformSettingsModel,
  })
    : Promise<TablePlatformSettingsModel> {
    return await (new UserApi())
      .registerPlatformSettings(data.user_id, data.settings)
      .pipe(take(1))
      .toPromise()
  }

  @Action({commit: 'setUser', rawError: true})
  clearData() {
    return null
  }


  @Action({rawError: true})
  async setSkills(skills: number[]) {
    return await (new SkillApi())
      .createForContestant(skills)
      .pipe(take(1))
      .toPromise();
  }

  @Action({rawError: true})
  async setPlatformSettings() {
    if (this.platformSettings) {
      if (this._user?.contestant?.id) {
        return await (new PlatformSettingsApi())
          .updateSettings(this.platformSettings)
          .pipe(take(1))
          .toPromise();
      }

      return await (new PlatformSettingsApi())
        .create(this.platformSettings)
        .pipe(take(1))
        .toPromise();
    }
  }

  @Action({commit: 'setUser', rawError: true})
  async fetchUser(): Promise<UserModel> {
    if (!!this.user) {
      return this.user;
    }
    return await (new UserApi())
      .me()
      .pipe(take(1))
      .toPromise();
  }

  @Action({rawError: true})
  async updateUser(): Promise<any> {
    const {first_name, last_name, email} = this._user || {};
    return await (new UserApi())
      .updateData({email, first_name, last_name})
      .pipe(take(1))
      .toPromise();
  }

  @Action({commit: 'setUser', rawError: true})
  resetUser() {
    return;
  }
}
